import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SocialAccount, ConnectSocialAccountRequest, UseSocialAccountsResult } from '@/types/social';
import { toast } from 'sonner';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';

export function useSocialAccounts(businessId: string | null): UseSocialAccountsResult {
  const queryClient = useQueryClient();

  const queryKey = ['social-accounts', businessId];

  const {
    data: accounts = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!businessId) return [];

      const { data, error } = await supabase
        .from('social_accounts')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as SocialAccount[];
    },
    enabled: !!businessId,
  });

  // Subscribe to realtime updates
  useEffect(() => {
    if (!businessId) return;

    const channel = supabase
      .channel(`social-accounts-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'social_accounts',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          // Skip invalidation if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          queryClient.invalidateQueries({ queryKey });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [businessId, queryClient, queryKey]);

  const connectAccount = async (request: ConnectSocialAccountRequest): Promise<SocialAccount> => {
    const { data, error } = await supabase
      .from('social_accounts')
      .insert({
        business_id: request.business_id,
        platform: request.platform,
        platform_account_id: 'pending', // Will be updated after OAuth
        access_token: request.access_token,
        refresh_token: request.refresh_token,
        token_expires_at: request.token_expires_at,
        page_id: request.page_id,
        page_access_token: request.page_access_token,
        status: 'pending',
        connected_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;

    queryClient.invalidateQueries({ queryKey });
    return data as SocialAccount;
  };

  const disconnectAccount = async (accountId: string): Promise<void> => {
    const { error } = await supabase
      .from('social_accounts')
      .delete()
      .eq('id', accountId);

    if (error) throw error;

    queryClient.invalidateQueries({ queryKey });
  };

  const refreshTokens = async (accountId: string): Promise<void> => {
    // Call Edge Function to refresh tokens
    const { error } = await supabase.functions.invoke('social-token-refresh', {
      body: { account_id: accountId }
    });

    if (error) throw error;

    queryClient.invalidateQueries({ queryKey });
  };

  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    accounts,
    isLoading,
    error: error as Error | null,
    connectAccount,
    disconnectAccount,
    refreshTokens,
    refresh,
  };
}

export default useSocialAccounts;
